import React, {
  useCallback, useRef, useEffect, MutableRefObject, useContext,
} from 'react';
import Webcam from 'react-webcam';
import { useDispatch, useSelector } from 'react-redux';

// import RecordRTC from 'recordrtc';
import {
  selectAudioId,
  selectCameraId, selectCameraReady,
  selectCameraRecording,
  selectStudioRecordCount,
  selectTeleprompterSync,
} from '../../../store/modules/studio/selectors';
import { UploadRecordContext } from '../record/UploadRecordContext';
import { detectIOS, padNumber } from '../../../utility/helpers';
import {
  setAutoScrolling, setCameraError,
  setCameraReady,
  setCameraRecording, setCountdownCamera,
  setRecordCount,
} from '../../../store/modules/studio/actions';

/* eslint-disable react-hooks/exhaustive-deps, @typescript-eslint/no-unused-vars */

type Props = {
  webcamRef: MutableRefObject<Webcam & HTMLVideoElement | null>;
};

const WebcamStreamCapture = ({ webcamRef }: Props) => {
  const isRecording = useSelector(selectCameraRecording);
  const recordCount = useSelector(selectStudioRecordCount);
  const cameraId = useSelector(selectCameraId);
  const audioId = useSelector(selectAudioId);
  const dispatch = useDispatch();
  const syncTeleprompter = useSelector(selectTeleprompterSync);
  const cameraReady = useSelector(selectCameraReady);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const { addStream } = useContext(UploadRecordContext);

  const handleDataAvailable = useCallback(
    ({ data }: BlobEvent) => {
      if (data.size > 0) {
        const name = `Recording-${padNumber(recordCount, 3)}`;
        addStream({
          name,
          blob: data,
        });
      }
    },
    [addStream, recordCount],
  );

  useEffect(() => {
    if (isRecording && !mediaRecorderRef.current) {
      if (webcamRef.current && webcamRef.current.stream) {
        mediaRecorderRef.current = new MediaRecorder(webcamRef.current.stream, {
          mimeType: 'video/webm',
          bitsPerSecond: 10e6, // BN: later we may want to map this to a UI quality setting
        });
        mediaRecorderRef.current.addEventListener(
          'dataavailable',
          handleDataAvailable,
        );
        mediaRecorderRef.current.addEventListener(
          'stop',
          (e) => console.log('!!!!Stop Recording event!!!!', e),
        );
        mediaRecorderRef.current.start();
      }
    } else if (!isRecording && mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current = null;
    }
  }, [isRecording, webcamRef, mediaRecorderRef, recordCount]);

  useEffect(() => () => {
    if (isRecording && mediaRecorderRef.current) {
      dispatch(setRecordCount(recordCount + 1));
      dispatch(setCameraRecording(false));
      if (syncTeleprompter) {
        dispatch(setAutoScrolling(false));
      }
      dispatch(setCountdownCamera(false));
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current = null;
    }
  }, [isRecording, syncTeleprompter, webcamRef.current]);

  const userMediaCb = useCallback(() => {
    if (!cameraReady) {
      dispatch(setCameraReady(true));
    }
  }, [dispatch, cameraReady]);

  const userMediaErrorCb = useCallback(() => {
    dispatch(setCameraError(!cameraReady));
  }, [dispatch, cameraReady]);


  return (
    <div style={{ display: 'flex', height: '100%', width: '100%' }}>
      { !(detectIOS() && process.env.REACT_APP_BUILD_ENV !== 'development')
      && (
        <Webcam
          height={720}
          width={1280}
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
          }}
          ref={webcamRef}
          onUserMedia={userMediaCb}
          onUserMediaError={userMediaErrorCb}
          videoConstraints={{
            width: 1920,
            height: 1080,
            frameRate: 30,
            deviceId: cameraId,
            resizeMode: 'none',
          }}
          audioConstraints={{
            deviceId: audioId,
          }}
        />
      )}
    </div>
  );
};

export default WebcamStreamCapture;
